import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import 'antd/dist/antd.css';
import '../../index.less';
import { Link, useHistory } from 'react-router-dom';
import {
  CalendarOutlined,
  SwapOutlined,
  GlobalOutlined,
  HomeOutlined,
  UserOutlined,
  FormOutlined,
  ProjectOutlined,
  SettingOutlined,
  TeamOutlined,
  ShareAltOutlined,
  BarChartOutlined,
  CarryOutOutlined,
  FileOutlined,
  FieldTimeOutlined,
  ToolOutlined,
  ContactsOutlined,
  GoldOutlined,
  GroupOutlined,
  ExperimentOutlined,
  TableOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  SearchOutlined,
  ProfileOutlined,
  MenuOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import {
  Breadcrumb,
  Layout,
  Row,
  Col,
  Popover,
  Divider,
  Dropdown,
  Menu,
} from 'antd';

import { HelpDropdown } from './HelpMenu';
import { NotificationsDropdown } from './notifications/Notifications';
import ProfileIcon from './ProfileIcon';
import OttoPill from '../../otto/OttoPill';

import NuxFocusBackground from '../../nux/NuxFocusBackground';
import NuxPopover from '../../nux/NuxPopover';
import IconPickerMap from '../IconPicker/iconPickerMap';

import {
  GET_STARTED_CLOSE_NUX_STEP_1,
  NEW_MENU_NUX_STEP_1,
  NEW_MENU_TYPE_TEXT,
} from '../../nux/nux.constants';

import WarningHeader from '../text/WarningHeader';
import { storageUpgradeWarning } from '../../files/fileHelpers';
import BorderlessButton from '../buttons/BorderlessButton';
import { toggleMenu } from '../../menu/state/menu.actions';
import { ReactComponent as SearchIcon } from '../../assets/icons/search.svg';
import Colors from '../../constants/Colors';
import { ReactComponent as UploadIcon } from '../../assets/icons/upload-icon.svg';
import { IMPORT_TYPE_OPTIONS } from '../../massImport/massImport.constants';
import WorkflowMarketplaceButton from './WorkflowMarketplaceButton';
import useToggle from '../hooks/useToggle';
import WorkflowMarketplace from '../../workflowMarketplace/WorkflowMarketplace';
import Permissions from '../../auth/Permissions';

const { Content, } = Layout;

const iconStyle = {
  marginRight:'5px',
  fontSize:20,
};

const getIcon = ({ icon }) => {
  switch (icon) {
    case 'timecard': return FieldTimeOutlined;
    case 'calendar': return CalendarOutlined;
    case 'swap': return SwapOutlined;
    case 'global': return GlobalOutlined;
    case 'home': return HomeOutlined;
    case 'user': return UserOutlined;
    case 'union': return TeamOutlined;
    case 'project': return ProjectOutlined;
    case 'setting': return SettingOutlined;
    case 'team': return TeamOutlined;
    case 'share-alt': return ShareAltOutlined;
    case 'bar-chart': return BarChartOutlined;
    case 'carry-out': return CarryOutOutlined;
    case 'file': return FileOutlined;
    case 'form': return FormOutlined;
    case 'equipment': return ToolOutlined;
    case 'address-book':
    case 'customers':
      return ContactsOutlined;
    case 'vendors': return CarryOutOutlined;
    case 'materials': return GoldOutlined;
    case 'group': return GroupOutlined;
    case 'analytics': return ExperimentOutlined;
    case 'table': return TableOutlined;
    case 'payables': return DollarOutlined;
    case 'environment': return EnvironmentOutlined;
    case 'search': return SearchOutlined;
    case 'profile': return ProfileOutlined;
    case 'import': return ImportOutlined;
    default: return IconPickerMap[icon];
  }
};

export default function BreadcrumbContainer({
  bodyStyle = {},
  redesignBodyStyle = {},
  fullscreen = false,
  crumbStyle = {},
  redesignCrumbStyle = {},
  crumbs = [],
  children,
  showHeader = true,
}) {
  const dispatch = useDispatch();
  const activeNuxAction = useSelector(state => state.nux.activeNuxAction);
  const { paidFlags = [], userId: ownerId, settings = {} } = useSelector((state) => state.settings.company);
  const history = useHistory();
  const {
    toggle: toggleWorkflowMarketplace,
    isToggled: isWorkflowMarketplaceOpen,
  } = useToggle();

  let crumbsView = [];
  crumbs.forEach((crumb,idx) => {
    const OurIcon = getIcon(crumb);
    const { style = {} } = crumb;
    crumbsView.push(
      (crumb.link) ?
      <Breadcrumb.Item key={idx.toString()} style={style}>
        <Link to={crumb.link} className='breadcrumb-link'>
          {OurIcon ? <OurIcon style={iconStyle}/> : ''}
          {crumb.text}
        </Link>
      </Breadcrumb.Item> :
      <Breadcrumb.Item key={idx.toString()} style={style}>
        {OurIcon ? <OurIcon style={iconStyle}/> : ''}
        {crumb.text}
      </Breadcrumb.Item>
    );
  });

  let redesignedCrumbs = [];
  crumbs.forEach((crumb,idx) => {
    const { style = {} } = crumb;
    redesignedCrumbs.push(
      (crumb.link) ?
      <Breadcrumb.Item key={idx.toString()} style={style}>
        <Link to={crumb.link} className='breadcrumb-link'>
          {crumb.text}
        </Link>
      </Breadcrumb.Item> :
      <Breadcrumb.Item key={idx.toString()} style={style}>
        {crumb.text}
      </Breadcrumb.Item>
    );
  });

  const isGetStartedNuxStep1 = activeNuxAction === GET_STARTED_CLOSE_NUX_STEP_1;
  const isNewMenuNux = activeNuxAction === NEW_MENU_NUX_STEP_1;
  const isAdvancedSearch = paidFlags.includes('ADVANCED_SEARCH');
  const isAbWorkReporting = paidFlags.includes('AB_WORK_REPORTING') && Permissions.has('AB_WORK_REPORTING');
  const hasDataImport = Permissions.has('DATA_IMPORT');
  const hasWorkflowMarketplaceImport = Permissions.has('IMPORT_WORKFLOW_MARKETPLACE');
  const isOwner = ownerId === Permissions.id;
  const hasRedesign = settings?.uxRedesignPt1;

  const SearchButton = hasRedesign ? SearchIcon : SearchOutlined;
  const advancedSearchButton = (
    <div className={hasRedesign ? 'header-container-redesign' : 'header-container'} onClick={() => { isAdvancedSearch && history.push('/search') }}>
      <SearchButton style={{ fontSize: 16 }} />
    </div>
  );

  const redesignStyle = hasRedesign ? {
    background: 'rgba(255, 255, 255, 1)',
    boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.04)',
  } : {};

  const importOptions = IMPORT_TYPE_OPTIONS.filter((option) => option.type === 'import');
  const bulkUpdateOptions = IMPORT_TYPE_OPTIONS.filter((option) => option.type === 'bulkUpdate');

  return (
    <Content style={{ position:'relative', margin: '0 0',paddingLeft:20,paddingRight:20, }}>
      <NuxFocusBackground show={activeNuxAction}/>
      { showHeader && (
        <>
          <Row
            flex='type'
            justify='space-between'
            style={{
              height: fullscreen ? 0 : 'auto',
              transition: '0.5s',
              position: 'relative',
              zIndex: 1,
              ...(fullscreen ? { overflow: 'hidden' } : {}),
              ...redesignStyle,
            }}
          >
            <Col>
              { !hasRedesign && (
                <Breadcrumb style={{ margin: '16px 24px', ...crumbStyle }}>
                  {crumbsView}
                </Breadcrumb>
              )}
              { hasRedesign && (
                <BorderlessButton
                  iconNode={<MenuOutlined />}
                  onClick={() => dispatch(toggleMenu())}
                  style={{
                    width: 'fit-content',
                    height: '100%',
                    background: 'inherit',
                  }}
                />
              )}
            </Col>
            <Col
              style={isNewMenuNux ? {
                zIndex: 100,
                backgroundColor: 'white',
              } : {}}
            >
              <NuxPopover
                visible={isNewMenuNux}
                text={NEW_MENU_TYPE_TEXT}
                placement='bottomRight'
              >
                <Row
                  type='flex'
                  style={{
                    margin: '16px 24px',
                    width: '100%',
                  }}
                  align='middle'
                  gutter={16}
                >
                  <Col>
                    <OttoPill />
                  </Col>
                  {hasWorkflowMarketplaceImport && (
                    <Col>
                      <WorkflowMarketplaceButton
                        openWorkflowMarketplace={toggleWorkflowMarketplace}
                      />
                    </Col>
                  )}
                  {!isAdvancedSearch && (
                    <Col>
                      <Popover
                        placement='bottomLeft'
                        trigger='hover'
                        title={<WarningHeader />}
                        content={storageUpgradeWarning({
                          title: 'Your account does not have access to advanced search',
                          isOwner,
                          action: 'use advanced search',
                        })}
                      >
                        {advancedSearchButton}
                      </Popover>
                    </Col>
                  )}
                  {!!isAdvancedSearch && (
                    <Col>
                      {advancedSearchButton}
                    </Col>
                  )}
                  {!!(isAbWorkReporting || hasDataImport) && (
                    <Col>
                      <Dropdown
                        overlay={(
                          <Menu>
                            <Menu.ItemGroup title="Import">
                              {isAbWorkReporting && (
                                <Menu.Item key="abwork" onClick={() => history.push('/abWorkImport')}>
                                  <ImportOutlined style={{ marginRight: 8 }} />
                                  AB Work Import
                                </Menu.Item>
                              )}
                              {isAbWorkReporting && hasDataImport && <Menu.Divider />}
                              {hasDataImport && importOptions.map((option) => {
                                const IconComponent = getIcon({ icon: option.icon });
                                return (
                                  <Menu.Item
                                    key={option.key}
                                    onClick={() => history.push(`/import/${option.key}`)}
                                  >
                                    {IconComponent && <IconComponent style={{ marginRight: 8 }} />}
                                    {option.label}
                                  </Menu.Item>
                                );
                              })}
                            </Menu.ItemGroup>
                            {hasDataImport && <Menu.Divider />}
                            {hasDataImport && (
                              <Menu.ItemGroup title="Bulk Update">
                                {bulkUpdateOptions.map((option) => {
                                  const IconComponent = getIcon({ icon: option.icon });
                                  return (
                                    <Menu.Item
                                      key={option.key}
                                      onClick={() => history.push(`/import/${option.key}`)}
                                    >
                                      {IconComponent && <IconComponent style={{ marginRight: 8 }} />}
                                      {option.label}
                                    </Menu.Item>
                                  );
                                })}
                              </Menu.ItemGroup>
                            )}
                          </Menu>
                        )}
                        trigger={['click']}
                      >
                        <div
                          className={hasRedesign ? 'header-container-redesign' : 'header-container'}
                          style={{ paddingLeft: 7, paddingTop: 7, cursor: 'pointer' }}
                        >
                          <UploadIcon />
                        </div>
                      </Dropdown>
                    </Col>
                  )}
                  <Col style={ hasRedesign ? { height: 32 } : {}}>
                    <NotificationsDropdown/>
                  </Col>
                  <Col>
                    <HelpDropdown
                      iconStyle={isGetStartedNuxStep1 ? { zIndex: 100, } : {}}
                      {...isGetStartedNuxStep1 ? { visible: true } : {}}
                    />

                  </Col>
                  <Col>
                    <ProfileIcon/>
                  </Col>
                </Row>
              </NuxPopover>
            </Col>
          </Row>

          {!hasRedesign && (
            <Row style={{
              width: '100%',
              height: fullscreen ? 0 : 1,
              opacity: fullscreen ? 0 : 1,
            }}>
              <Divider style={{
                backgroundColor: Colors.ONTRACCR_BLACK,
                height: 1,
                marginTop: 0,
              }}/>
            </Row>
          )}
        </>
      )}

      <div
        className="breadcrumb-page-body"
        style={{
          margin: 15,
          minHeight: 280,
          height: fullscreen ? 'calc(100vh - 30px)' : '84vh',
          ...bodyStyle,
          ...(hasRedesign ? redesignBodyStyle : {}),
        }}
      >
          { !fullscreen && hasRedesign && (
            <div>
              <Breadcrumb
                className="page-title"
                style={{
                  margin: '16px 0px',
                  ...crumbStyle,
                  ...redesignCrumbStyle,
                }}
              >
                {redesignedCrumbs}
              </Breadcrumb>
            </div>
          )}
          {children}
          {hasWorkflowMarketplaceImport && (
            <WorkflowMarketplace
              open={isWorkflowMarketplaceOpen}
              onClose={toggleWorkflowMarketplace}
            />
          )}
      </div>
      </Content>
  );
}
